import { useEffect, useState } from "react";
import { format, parseISO } from "date-fns";
import { it } from "date-fns/locale";
import { Newspaper, Trash2 } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { lang: "it", title: "", slug: "", excerpt: "", content: "", published: false };
const LANGS = ["it", "de", "en", "fr"];

export const AdminBlog = ({ client, guard }) => {
  const [posts, setPosts] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const load = () => guard(client.get("/admin/posts").then((r) => setPosts(r.data)));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    await guard(
      (editing
        ? client.patch(`/admin/posts/${editing}`, form).then(() => "Articolo aggiornato.")
        : client.post("/admin/posts", form).then(() => "Articolo creato.")
      ).then((msg) => {
        toast.success(msg);
        setForm(EMPTY);
        setEditing(null);
        setShowForm(false);
        load();
      })
    );
  };

  const edit = (p) => {
    setEditing(p.id);
    setForm({ lang: p.lang, title: p.title, slug: p.slug, excerpt: p.excerpt || "", content: p.content, published: p.published });
    setShowForm(true);
  };

  const togglePub = async (p) => {
    await guard(client.patch(`/admin/posts/${p.id}`, { published: !p.published }).then(() => {
      setPosts((ps) => ps.map((x) => (x.id === p.id ? { ...x, published: !p.published } : x)));
      toast.success(p.published ? "Articolo ritirato (bozza)." : "Articolo pubblicato.");
    }));
  };

  const del = async (p) => {
    await guard(client.delete(`/admin/posts/${p.id}`).then(() => {
      setPosts((ps) => ps.filter((x) => x.id !== p.id));
      toast.success("Articolo eliminato.");
    }));
  };

  return (
    <div className="space-y-4" data-testid="admin-blog">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm font-semibold text-antracite/60">
          {posts.length} {posts.length === 1 ? "articolo" : "articoli"} — pubblicati in italiano, tedesco, inglese o francese.
        </p>
        <button
          type="button"
          onClick={() => { setShowForm(!showForm); setEditing(null); setForm(EMPTY); }}
          className="btn-primary !px-5 !py-2.5 !text-xs"
          data-testid="admin-post-new"
        >
          <Newspaper className="h-4 w-4" /> {showForm ? "Chiudi" : "Nuovo articolo"}
        </button>
      </div>

      {showForm && (
        <form onSubmit={save} className="card-soft space-y-4 p-6" data-testid="admin-post-form">
          <div className="grid gap-4 sm:grid-cols-3">
            <div>
              <label className="mb-1 block text-xs font-bold uppercase tracking-[0.12em] text-antracite/60">Lingua *</label>
              <select value={form.lang} onChange={(e) => set("lang", e.target.value)} className="input-field" data-testid="admin-post-lang">
                {LANGS.map((l) => <option key={l} value={l}>{l.toUpperCase()}</option>)}
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="mb-1 block text-xs font-bold uppercase tracking-[0.12em] text-antracite/60">Titolo *</label>
              <input required minLength={3} maxLength={140} value={form.title} onChange={(e) => set("title", e.target.value)} className="input-field" data-testid="admin-post-title" />
            </div>
          </div>
          <div>
            <label className="mb-1 block text-xs font-bold uppercase tracking-[0.12em] text-antracite/60">Slug (facoltativo — generato dal titolo)</label>
            <input value={form.slug} onChange={(e) => set("slug", e.target.value)} className="input-field" placeholder="es. consigli-sito-web" data-testid="admin-post-slug" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-bold uppercase tracking-[0.12em] text-antracite/60">Estratto (anteprima, max 300 caratteri)</label>
            <textarea rows={2} maxLength={300} value={form.excerpt} onChange={(e) => set("excerpt", e.target.value)} className="input-field resize-none" data-testid="admin-post-excerpt" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-bold uppercase tracking-[0.12em] text-antracite/60">Contenuto * (riga vuota tra i paragrafi, "## " per i sottotitoli, "- " per gli elenchi, **grassetto**)</label>
            <textarea required minLength={50} rows={12} value={form.content} onChange={(e) => set("content", e.target.value)} className="input-field resize-y font-mono text-xs" data-testid="admin-post-content" />
          </div>
          <label className="flex items-center gap-3 text-sm text-antracite/70">
            <input type="checkbox" checked={form.published} onChange={(e) => set("published", e.target.checked)} className="h-4 w-4 rounded accent-petrolio" data-testid="admin-post-published" />
            Pubblica subito (altrimenti resta bozza)
          </label>
          <button type="submit" className="btn-primary" data-testid="admin-post-save">
            {editing ? "Salva modifiche" : "Crea articolo"}
          </button>
        </form>
      )}

      {posts.map((p) => (
        <div key={p.id} className="card-soft flex flex-wrap items-center justify-between gap-3 p-5" data-testid={`admin-post-${p.id.slice(0, 8)}`}>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-full bg-antracite/10 px-2.5 py-0.5 text-[0.65rem] font-extrabold uppercase text-antracite/70">{p.lang}</span>
              <span className={`rounded-full px-2.5 py-0.5 text-[0.65rem] font-extrabold ${p.published ? "bg-acqua text-petrolio" : "bg-amber-100 text-amber-800"}`} data-testid={`admin-post-status-${p.id.slice(0, 8)}`}>
                {p.published ? "Pubblicato" : "Bozza"}
              </span>
              <span className="text-xs text-antracite/50">{format(parseISO(p.created_at), "d MMM yyyy", { locale: it })}</span>
            </div>
            <p className="mt-1.5 truncate font-bold text-antracite">{p.title}</p>
            <p className="text-xs text-antracite/50">/blog/{p.slug}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={() => edit(p)} className="rounded-full bg-acqua px-4 py-2 text-xs font-bold text-petrolio transition-colors hover:bg-salvia/60" data-testid={`admin-post-edit-${p.id.slice(0, 8)}`}>Modifica</button>
            <button type="button" onClick={() => togglePub(p)} className="rounded-full bg-petrolio px-4 py-2 text-xs font-bold text-avorio transition-colors hover:bg-petrolioDeep" data-testid={`admin-post-toggle-${p.id.slice(0, 8)}`}>
              {p.published ? "Ritira" : "Pubblica"}
            </button>
            <button type="button" onClick={() => del(p)} className="inline-flex items-center gap-1.5 rounded-full bg-red-50 px-4 py-2 text-xs font-bold text-red-700 transition-colors hover:bg-red-100" data-testid={`admin-post-delete-${p.id.slice(0, 8)}`}>
              <Trash2 className="h-3.5 w-3.5" /> Elimina
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};
